/**
 * Editor bbox utilities — pure functions over the IR's bbox geometry.
 *
 * Extracted from editor.ts in Phase EB.2 of the editor-ts-decompose Epic
 * (design doc: docs/design/editor-ts-decompose-plan-260522.md §"Phase EB.2"
 * + §9 cross-cluster dependency 3).
 *
 * Why a dedicated module: these two helpers are shared between Cluster D
 * (render — `renderOverlays` and `renderOverlaysForPage` both reach for
 * `filterAndOverrideBboxesAgainstIr`) and Cluster G (insert-flow — the
 * page-controller wiring calls `buildPageBboxMap` for the per-page bbox
 * lookup that drag/resize/rubber-band controllers need). Owning them in
 * a third module avoids a Cluster D → Cluster G (or vice-versa) import
 * cycle when Phase EB.4 extracts the insert-flow cluster.
 *
 * **Phase FB Q1 ruling (frontend-mv-architecture-260524.md §9):** This
 * file stays classified as `util` in the editor layer table; per the
 * Flux/Redux layer rules `util` MUST NOT import from `store/`. The
 * previous shape imported `state` from `editor-state.ts` (the store)
 * and read `state.ir` inside both functions — a runtime layer
 * violation. The fix accepts the IR `Document` as a parameter (or via
 * a getter callback) so callers in `views/` / `controllers/` pass the
 * needed slice of state in. This is the canonical "util receives data,
 * doesn't reach for it" pattern.
 *
 * Both functions remain PURE relative to their `ir` input — no side
 * effects, no module-scope mutation, no DOM. They're safe to call from
 * any thread of the editor's evaluation, and unit-testable in isolation
 * with a stub IR fixture (no `state` injection needed).
 */
import { reconcileBboxesWithIr } from "./ir-bbox-sync.js";
/**
 * Filter the WASM-reported bbox list against the local IR, dropping
 * entries for structs that have been locally deleted and overriding the
 * bbox coordinates for structs that have been locally moved.
 *
 * The WASM bbox cache reflects the SERVER-SIDE struct tree. Local
 * client-only ops (delete-element, set-bbox, create-element) skip the
 * WASM round-trip, so the WASM list still contains entries for
 * locally-deleted structs (and the bboxes for locally-moved structs are
 * stale). The local IR is the source of truth between save dispatches.
 *
 * @param wasmBoxes Per-page bbox list returned by the WASM bbox cache.
 * @param ir        IR Document — caller passes `state.ir` (or a test
 *                  fixture). WHY param-accept: Phase FB layer rule —
 *                  util must not reach into the store layer.
 * @param pageNum   1-based page number for the bbox list.
 * @returns A new bbox list with deleted entries removed and moved entries
 *   overridden by IR-recorded coordinates.
 */
export function filterAndOverrideBboxesAgainstIr(wasmBoxes, ir, pageNum) {
    return reconcileBboxesWithIr(wasmBoxes, ir, pageNum);
}
/**
 * Find the structIds of top-level IR blocks on `pageNum` whose card lists on
 * the page but which have NO entry in the reconciled `boxes` list — i.e.
 * elements that show a card on the right pane but would draw no overlay on the
 * left. Bug 5 / INV-OVL-1: the caller appends a degenerate placeholder for
 * each so the overlay layer renders a visible locate-affordance (expand, never
 * silently omit — rule #8). Container-internal items already represented by
 * their container's wrapping box (`suppressedStructIds`) are excluded so the
 * page doesn't gain a stack of redundant markers.
 *
 * PURE relative to its inputs — returns data; the render layer logs + mutates.
 *
 * @param boxes               Reconciled per-page bbox list (post
 *                            `filterAndOverrideBboxesAgainstIr`).
 * @param ir                  IR Document — caller passes `state.ir`.
 * @param pageNum             1-based page number.
 * @param suppressedStructIds Container-internal children to exclude (or null).
 * @returns structIds of carded, on-page elements absent from `boxes`.
 */
export function findIrCardStructIdsMissingBox(boxes, ir, pageNum, suppressedStructIds) {
    const out = [];
    if (!ir || !Array.isArray(ir.blocks))
        return out;
    const present = new Set();
    if (Array.isArray(boxes)) {
        for (const b of boxes) {
            if (b && typeof b.structId === "string")
                present.add(b.structId);
        }
    }
    for (const block of ir.blocks) {
        if (!block || typeof block.structId !== "string" || block.structId.length === 0)
            continue;
        const onPage = (block.page ?? 1) === pageNum;
        if (!onPage)
            continue;
        if (present.has(block.structId))
            continue;
        if (suppressedStructIds && suppressedStructIds.has(block.structId))
            continue;
        out.push(block.structId);
    }
    return out;
}
/**
 * Build the per-page bbox lookup map keyed by structId. Used by the
 * insert-flow page controllers (drag-move, resize-move, rubber-band,
 * draw-create) to resolve a structId to its `[x, y, w, h]` PDF-user-space
 * coordinates without re-walking the whole IR on every gesture.
 *
 * Composite blocks (currently just List) are flattened so dragging a
 * child cell or list item works the same as a top-level block.
 *
 * @param ir       IR Document — caller passes `state.ir` (or a test
 *                 fixture). WHY param-accept: Phase FB layer rule —
 *                 util must not reach into the store layer.
 * @param pageNum  1-based page number to filter by.
 * @returns A Map of structId → [x, y, w, h] for every block on that page.
 *   Returns an empty map if the IR is unset or has no blocks.
 */
export function buildPageBboxMap(ir, pageNum) {
    const out = new Map();
    if (!ir || !Array.isArray(ir.blocks))
        return out;
    function visit(block) {
        if (!block)
            return;
        const onPage = (block.page ?? 1) === pageNum;
        if (onPage && block.bbox) {
            const b = block.bbox;
            if (typeof b.x === "number" && typeof b.y === "number"
                && typeof b.w === "number" && typeof b.h === "number") {
                out.set(block.structId, [b.x, b.y, b.w, b.h]);
            }
        }
        // Recurse into composites so the user can drag table cells / list items.
        if (block.kind === "List" && Array.isArray(block.items)) {
            for (const item of block.items) {
                if (Array.isArray(item.content)) {
                    for (const ib of item.content)
                        visit(ib);
                }
            }
        }
    }
    for (const b of ir.blocks)
        visit(b);
    return out;
}
/**
 * Compute the set of structIds whose LEFT-pane PDF-overlay box should be
 * SUPPRESSED because they are the internal layout of a container that already
 * carries its own wrapping box.
 *
 * Motivation (editor-structure-overlay-ux-260904): a `List` container's child
 * paragraphs each carry a derived (MCID / `/A[/Layout]/BBox`) bbox, so
 * `collectIrBboxes` emits one overlay box per item — a cluttered stack of
 * rectangles on top of the one List box. The user directive is "if we have a
 * box of the list, make the internal layout of things invisible on the left,
 * just show the wrapping box, and permit visibility of the items on the right."
 * The right-pane card view (`cards.ts` `ListItemPreview`) already renders +
 * edits those items, so they stay reachable there.
 *
 * Signal (TS-side container-child inference — no C# flag threaded): a block is
 * suppressed iff it is a descendant (via `List.items[].content`, recursively)
 * of a `List` block that HAS its own bbox AND the descendant's own bbox is
 * CONTAINED within that List's bbox (the internal-layout case). A descendant
 * whose bbox extends outside the container by more than {@link _CONTAINMENT_TOL_PT}
 * carries REAL distinct geometry (an independently-positioned inner shape) and
 * is NOT suppressed — it still draws on the left. A List with no bbox has no
 * wrapping box to represent its items, so its children are left visible (never
 * suppress into nothing).
 *
 * The container box itself is never a descendant of itself, so it is never in
 * the returned set — it always draws.
 *
 * PURE relative to `ir`: no DOM, no module-scope mutation. `pageNum` filters to
 * one page (the overlay renders a single page at a time).
 *
 * @param ir       IR Document — caller passes `state.ir` (or a test fixture).
 * @param pageNum  1-based page number; only blocks on this page are considered.
 * @returns A Set of structIds to suppress on the left pane (empty if none).
 */
export function computeContainerSuppressedStructIds(ir, pageNum) {
    const out = new Set();
    if (!ir || !Array.isArray(ir.blocks))
        return out;
    // Collect every suppressible descendant structId under one container bbox.
    function collectDescendants(container, block) {
        if (!block)
            return;
        if (typeof block.structId === "string" && block.structId.length > 0
            && _bboxContains(container.bbox, block.bbox)) {
            out.add(block.structId);
        }
        // Recurse through nested composites so a list-in-a-list's descendants
        // are also folded into the outer container's internal layout.
        if (block.kind === "List" && Array.isArray(block.items)) {
            for (const item of block.items) {
                if (Array.isArray(item.content)) {
                    for (const ib of item.content)
                        collectDescendants(container, ib);
                }
            }
        }
    }
    for (const block of ir.blocks) {
        if (!block)
            continue;
        const onPage = (block.page ?? 1) === pageNum;
        if (!onPage)
            continue;
        // Container-with-box: a List that carries its own wrapping bbox.
        if (block.kind === "List" && block.bbox && Array.isArray(block.items)) {
            for (const item of block.items) {
                if (Array.isArray(item.content)) {
                    for (const ib of item.content)
                        collectDescendants(block, ib);
                }
            }
        }
    }
    return out;
}
/** Tolerance (PDF points) for the container-containment test. A child whose
 *  bbox exceeds its container by more than this on any side is treated as
 *  independently placed (REAL distinct geometry) and is NOT suppressed. */
const _CONTAINMENT_TOL_PT = 2.0;
/**
 * True when `childBbox` is contained within `containerBbox` (both `BboxXywh`-
 * shaped, `{x,y,w,h}` PDF user-space) within {@link _CONTAINMENT_TOL_PT}.
 * Returns false when either bbox is missing or malformed — a child with no
 * derivable bbox is never suppressed (it is already not drawn by the overlay
 * loop).
 */
function _bboxContains(containerBbox, childBbox) {
    const c = containerBbox;
    const k = childBbox;
    if (!c || !k)
        return false;
    if (typeof c.x !== "number" || typeof c.y !== "number"
        || typeof c.w !== "number" || typeof c.h !== "number")
        return false;
    if (typeof k.x !== "number" || typeof k.y !== "number"
        || typeof k.w !== "number" || typeof k.h !== "number")
        return false;
    const tol = _CONTAINMENT_TOL_PT;
    return k.x >= c.x - tol
        && k.y >= c.y - tol
        && (k.x + k.w) <= (c.x + c.w) + tol
        && (k.y + k.h) <= (c.y + c.h) + tol;
}
